import React, { useEffect, useState } from 'react';
import { hrmsListPayroll, hrmsRunPayroll, hrmsEmailPayslip, hrmsBulkEmailPayslips, hrmsGetJob } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { generatePayslipPdf } from '../../lib/payslipPdf';
import { PlayCircle, Download, Loader2, Mail, Send } from 'lucide-react';

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const PayrollPage = () => {
  const { toast } = useToast();
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [bulkJob, setBulkJob] = useState(null); // { id, total, processed, sent, failed, skipped, status }

  const load = async () => setRows(await hrmsListPayroll({ month }).catch(() => []));
  useEffect(() => { load(); }, [month]);

  // Poll bulk job every 1.5s until done
  useEffect(() => {
    if (!bulkJob || bulkJob.status === 'completed') return;
    const t = setInterval(async () => {
      try {
        const j = await hrmsGetJob(bulkJob.id);
        setBulkJob(j);
        if (j.status === 'completed') {
          clearInterval(t);
          toast({ title: 'Bulk email complete', description: `${j.sent} sent · ${j.failed} failed · ${j.skipped} skipped` });
          load();
        }
      } catch (err) { clearInterval(t); }
    }, 1500);
    return () => clearInterval(t);
  }, [bulkJob?.id]);

  const run = async () => {
    if (!window.confirm(`Run payroll for ${month}? This will (re)compute payslips for all active employees.`)) return;
    setRunning(true);
    try {
      const res = await hrmsRunPayroll({ month });
      toast({ title: `Payroll processed`, description: `${res.count} payslips generated for ${res.month}` });
      load();
    } catch (err) {
      toast({ title: 'Payroll failed', description: err?.response?.data?.detail || '' });
    } finally { setRunning(false); }
  };

  const totalNet = rows.reduce((s, r) => s + (r.net || 0), 0);
  const totalGross = rows.reduce((s, r) => s + (r.gross || 0), 0);

  return (
    <div data-testid="payroll-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold">Payroll</h1>
          <p className="text-sm text-slate-500">Auto-generated payslips based on attendance and approved leaves.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label>Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10" data-testid="payroll-month" />
          </div>
          <Button onClick={run} disabled={running} className="h-10 bg-orange-500 hover:bg-orange-600" data-testid="payroll-run">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4 mr-1" /> Run payroll</>}
          </Button>
          <Button onClick={async () => {
            if (!rows.length) { toast({ title: 'No payslips to email' }); return; }
            const unsent = rows.filter(r => !r.emailed_at).length;
            const only = unsent > 0 && window.confirm(`Send only the ${unsent} unsent payslips (Cancel to send ALL ${rows.length})?`);
            try {
              const j = await hrmsBulkEmailPayslips({ month, only_unsent: only });
              setBulkJob(j);
              toast({ title: 'Bulk email started', description: `Queued ${j.total} payslips` });
            } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || '' }); }
          }} disabled={!!bulkJob && bulkJob.status !== 'completed'} className="h-10 bg-blue-600 hover:bg-blue-700" data-testid="payroll-email-all">
            <Send className="h-4 w-4 mr-1" /> Email all payslips
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white border border-slate-200 p-4"><div className="text-xs uppercase text-slate-500">Payslips</div><div className="text-2xl font-extrabold">{rows.length}</div></div>
        <div className="rounded-2xl bg-white border border-slate-200 p-4"><div className="text-xs uppercase text-slate-500">Total gross</div><div className="text-2xl font-extrabold">{totalGross.toLocaleString('en-IN')}</div></div>
        <div className="rounded-2xl bg-white border border-slate-200 p-4"><div className="text-xs uppercase text-slate-500">Total net pay</div><div className="text-2xl font-extrabold text-emerald-600">{totalNet.toLocaleString('en-IN')}</div></div>
      </div>

      {bulkJob && (
        <div className="mt-4 rounded-2xl bg-blue-50 border border-blue-200 p-4" data-testid="bulk-job-progress">
          <div className="flex items-center justify-between text-sm">
            <div className="font-semibold text-blue-900">Bulk emailing payslips…</div>
            <div className="text-blue-700 font-mono">{bulkJob.processed}/{bulkJob.total} · {bulkJob.status}</div>
          </div>
          <div className="h-2.5 bg-blue-100 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${Math.round((bulkJob.processed / Math.max(bulkJob.total, 1)) * 100)}%` }} />
          </div>
          <div className="mt-2 flex gap-4 text-xs text-slate-600">
            <span>✅ {bulkJob.sent} sent</span>
            <span>⚠️ {bulkJob.failed} failed</span>
            <span>⏭️ {bulkJob.skipped} skipped</span>
            {bulkJob.status === 'completed' && <button onClick={() => setBulkJob(null)} className="ml-auto text-blue-700 underline">Dismiss</button>}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">Payslip</th>
            <th className="text-left px-4 py-3">Employee</th>
            <th className="text-left px-4 py-3">Days</th>
            <th className="text-right px-4 py-3">Gross</th>
            <th className="text-right px-4 py-3">Deductions</th>
            <th className="text-right px-4 py-3">Net</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-10">No payroll for {month}. Click "Run payroll".</td></tr>}
            {rows.map((p) => {
              const ded = (p.deductions?.tax || 0) + (p.deductions?.pf || 0);
              return (
                <tr key={p.id} className="border-t">
                  <td className="px-4 py-3 font-mono text-xs">{p.payslip_id}</td>
                  <td className="px-4 py-3 font-medium">{p.employee_name}<div className="text-xs text-slate-500">{p.employee_id}</div></td>
                  <td className="px-4 py-3">{p.paid_days}/{p.total_days}</td>
                  <td className="px-4 py-3 text-right">{(p.gross || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right text-red-600">-{ded.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-600">{(p.net || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3"><Badge className="bg-emerald-500">{p.status}</Badge></td>
                  <td className="px-4 py-3 space-x-1">
                    <Button variant="outline" onClick={() => generatePayslipPdf(p)} className="h-8 px-2" data-testid={`payroll-pdf-${p.payslip_id}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                    <Button variant="outline" onClick={async () => { try { await hrmsEmailPayslip(p.id); toast({ title: 'Payslip emailed', description: p.employee_email }); load(); } catch (e) { toast({ title: 'Email failed', description: e?.response?.data?.detail || '' }); } }} className="h-8 px-2" data-testid={`payroll-email-${p.payslip_id}`}><Mail className="h-3.5 w-3.5 mr-1" /> {p.emailed_at ? 'Resend' : 'Email'}</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayrollPage;
